/**
 * EmergencyConfirmModal - 2026-06-07 settings-emergency-quotes-refresh-final.
 *
 * Shared typed-confirmation modal for the 4 emergency control actions.
 * The submit button stays disabled until the user types the EXACT
 * confirmation phrase. On submit, posts to the matching backend
 * endpoint, surfaces success/failure inline, then calls onDone().
 *
 * Replaces the legacy window.confirm() stubs that "looked" like they
 * did something but were actually no-ops. No alert(), no confirm().
 */
import React, { useEffect, useState } from "react";
import { AlertTriangle, X, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { apiV1Base } from "../../lib/apiConfig";

export type EmergencyAction =
  | "close_all_positions"
  | "cancel_all_orders"
  | "disable_trading"
  | "reset_allocation";

export const EMERGENCY_PHRASES: Record<EmergencyAction, string> = {
  close_all_positions: "CLOSE ALL POSITIONS",
  cancel_all_orders: "CANCEL ALL ORDERS",
  disable_trading: "DISABLE TRADING",
  reset_allocation: "RESET ALLOCATION",
};

const ROUTE: Record<EmergencyAction, string> = {
  close_all_positions: "/user/emergency/close-all-positions",
  cancel_all_orders: "/user/emergency/cancel-all-orders",
  disable_trading: "/user/emergency/disable-trading",
  reset_allocation: "/user/emergency/reset-portfolio-allocation",
};

const TITLE: Record<EmergencyAction, string> = {
  close_all_positions: "Close all positions",
  cancel_all_orders: "Cancel all open orders",
  disable_trading: "Disable trading",
  reset_allocation: "Reset strategy allocation",
};

const IMPACT: Record<EmergencyAction, string> = {
  close_all_positions:
    "Submits an atomic flatten to your connected Alpaca Paper account. Every open position is closed with a paper market order. No new buy-to-open orders are submitted.",
  cancel_all_orders:
    "Cancels every working paper order on your Alpaca Paper account. Filled orders are unaffected.",
  disable_trading:
    "Sets the trading_disabled flag on your account. Downstream execution must consult this flag before placing any order. Re-enable from settings.",
  reset_allocation:
    "Marks your strategy allocation table for reset. Capital allocated to specific strategies is released back to your cash bucket. Does not touch open positions.",
};

export interface EmergencyConfirmModalProps {
  open: boolean;
  action: EmergencyAction | null;
  onClose: () => void;
  onDone?: (result: { audit_id: string; status: string }) => void;
}

export default function EmergencyConfirmModal({
  open,
  action,
  onClose,
  onDone,
}: EmergencyConfirmModalProps) {
  const [typed, setTyped] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{
    kind: "success" | "error";
    msg: string;
  } | null>(null);
  // 2026-06-07 r-batch: dry-run preview before final action.
  const [preview, setPreview] = useState<any | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setTyped("");
      setSubmitting(false);
      setResult(null);
      setPreview(null);
      setPreviewLoading(false);
      setPreviewError(null);
    }
  }, [open]);

  // Auto-fetch preview when the modal opens.
  useEffect(() => {
    if (!open || !action) return;
    let cancel = false;
    const fetchPreview = async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const token = typeof localStorage !== "undefined"
          ? localStorage.getItem("adversyn_auth_token") : null;
        if (!token) {
          if (!cancel) {
            setPreviewError("Not authenticated");
            setPreviewLoading(false);
          }
          return;
        }
        const r = await fetch(apiV1Base() + "/user/emergency/preview", {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "omit",
        });
        const body = await r.json().catch(() => ({}));
        if (cancel) return;
        if (!r.ok) {
          setPreviewError(body?.error?.message || ("HTTP " + r.status));
        } else {
          setPreview(body?.data ?? body);
        }
      } catch (e: any) {
        if (!cancel) setPreviewError(String(e?.message || e));
      } finally {
        if (!cancel) setPreviewLoading(false);
      }
    };
    void fetchPreview();
    return () => { cancel = true; };
  }, [open, action]);

  if (!open || !action) return null;
  const phrase = EMERGENCY_PHRASES[action];
  const enabled = !submitting && typed === phrase
    && preview !== null && !previewError && !previewLoading;

  const onSubmit = async () => {
    if (!enabled) return;
    setSubmitting(true);
    setResult(null);
    try {
      const token = typeof localStorage !== "undefined"
        ? localStorage.getItem("adversyn_auth_token") : null;
      if (!token) {
        setResult({ kind: "error", msg: "Not authenticated" });
        setSubmitting(false);
        return;
      }
      const r = await fetch(apiV1Base() + ROUTE[action], {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        credentials: "omit",
        body: JSON.stringify({ confirmation_text: typed }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        const msg = body?.error?.message
          || body?.message
          || `HTTP ${r.status}`;
        setResult({ kind: "error", msg: String(msg) });
        setSubmitting(false);
        return;
      }
      const data = body?.data ?? body;
      const auditId = String(data?.audit_id || "");
      const status = String(data?.status || "");
      setResult({ kind: "success", msg: `Done. Audit ${auditId}` });
      // Refetch portfolio after a state-changing emergency action.
      try {
        const ev = await import("../../lib/refreshEvents");
        ev.emitRefresh("portfolio", { source: "emergency:" + action });
        ev.emitRefresh("brokerage", { source: "emergency:" + action });
      } catch { /* ignore */ }
      onDone?.({ audit_id: auditId, status });
      setSubmitting(false);
    } catch (e: any) {
      setResult({ kind: "error", msg: String(e?.message || e) });
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="emergency-modal"
      data-action={action}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-lg bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl shadow-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle size={20} className="text-rose-600" />
            <h3 data-testid="emergency-modal-title" className="text-base font-black uppercase italic text-[var(--app-text)]">
              {TITLE[action]}
            </h3>
          </div>
          <button
            type="button"
            data-testid="emergency-modal-close"
            onClick={onClose}
            className="p-1 rounded-md hover:bg-[var(--input-bg)] text-[var(--muted-text)]"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <p data-testid="emergency-modal-impact" className="text-xs text-[var(--muted-text)] mb-4 leading-relaxed">
          {IMPACT[action]}
        </p>

        <div data-testid="emergency-modal-preview" className="mb-3 p-3 rounded-lg border border-[var(--card-border)] bg-[var(--input-bg)]">
          <div className="text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)] mb-2">Dry-run preview (no broker call yet)</div>
          {previewLoading && (
            <div className="text-xs font-bold text-[var(--muted-text)]">Loading preview…</div>
          )}
          {previewError && (
            <div data-testid="emergency-modal-preview-error" className="text-xs font-bold text-rose-600">Preview failed: {previewError}</div>
          )}
          {preview && !previewLoading && !previewError && (
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-[var(--muted-text)]">Open positions</span>
                <span data-testid="emergency-modal-positions-count" className="font-mono font-black text-[var(--app-text)]">
                  {typeof preview.open_positions_count === "number" ? preview.open_positions_count : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[var(--muted-text)]">Open orders</span>
                <span data-testid="emergency-modal-orders-count" className="font-mono font-black text-[var(--app-text)]">
                  {typeof preview.open_orders_count === "number" ? preview.open_orders_count : "—"}
                </span>
              </div>
              <div className="flex items-center justify-between col-span-2">
                <span className="text-[var(--muted-text)]">Broker mode</span>
                <span className="font-mono text-[var(--app-text)]">{preview.mode || "—"} / {preview.broker_provider || "—"}</span>
              </div>
            </div>
          )}
        </div>
        <label className="block mb-2 text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)]">
          Type <span className="text-rose-600">{phrase}</span> to confirm
        </label>
        <input
          data-testid="emergency-modal-input"
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoFocus
          disabled={submitting}
          placeholder={phrase}
          className="w-full mb-4 px-3 py-2 rounded-lg border border-[var(--input-border)] bg-[var(--input-bg)] text-sm font-mono text-[var(--app-text)] focus:outline-none focus:border-rose-500 disabled:opacity-50"
        />

        {result && (
          <div
            data-testid={result.kind === "success" ? "emergency-modal-success" : "emergency-modal-error"}
            className={
              "mb-4 px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-2 " +
              (result.kind === "success"
                ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-700"
                : "bg-rose-500/10 border border-rose-500/30 text-rose-700")
            }
          >
            {result.kind === "success"
              ? <CheckCircle2 size={14} />
              : <XCircle size={14} />}
            {result.msg}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            data-testid="emergency-modal-cancel"
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-2 rounded-lg text-xs font-black uppercase tracking-widest border border-[var(--card-border)] text-[var(--muted-text)] hover:bg-[var(--input-bg)] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="emergency-modal-submit"
            onClick={onSubmit}
            disabled={!enabled}
            className="px-3 py-2 rounded-lg text-xs font-black uppercase tracking-widest bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {submitting && <Loader2 size={12} className="animate-spin" />}
            {submitting ? "Submitting" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}
